import { useAppStore } from "@/global/store/store";
import {
  CartManagerState,
  selectCartManager,
} from "@/global/store/data/purchaseSeats/cartManager";
import { useBackendSubtotals } from "@/shared/modules/PurchaseSeats/BottomBar/useBackendSubtotlals";
import { TeamConfig, useTeamConfig } from "@/global/hooks/useTeamConfig";
import { ServiceFeeState } from "@/global/store/data/purchaseSeats/serviceFee";
import { vendorUUIDs } from "@/global/config/constants";
import { CreditState } from "@/global/store/data/purchaseSeats/credit";
import { usePaymentPlans } from "../../PaymentPlans/usePaymentPlans";
import { PaymentPlansType } from "@/types";

function calcAllInPrice(
  cartState: CartManagerState,
  serviceFeeState: ServiceFeeState,
  selectedPaymentPlan: PaymentPlansType | undefined,
  teamConfig: TeamConfig
) {
  let allInPrice = cartState?.allInPrice || 0;
  const isTicketmaster = teamConfig.platform === vendorUUIDs.ticketmaster;
  if (isTicketmaster) allInPrice = serviceFeeState?.totalAmount;

  if (selectedPaymentPlan?.id !== 0) {
    let planAmount = selectedPaymentPlan?.installments[0].amount || 0;
    if (planAmount > 0) allInPrice = allInPrice - planAmount;
  }
  return allInPrice;
}

function calcCreditToBeApplied(
  creditState: CreditState,
  teamConfig: TeamConfig
) {
  const isCreditEnabled = Boolean(teamConfig?.availableCreditEnabled);
  const { creditInLedger, creditToUse } = creditState;
  return isCreditEnabled ? Math.max(creditInLedger, creditToUse) : 0;
}

function calcAmountDue(
  serviceFeeState: ServiceFeeState,
  creditState: CreditState,
  teamConfig: TeamConfig
) {
  let amountDue = serviceFeeState?.totalAmount;
  if (teamConfig?.availableCreditEnabled) {
    amountDue = serviceFeeState?.totalAmount - creditState?.creditToUse;
  }
  return amountDue;
}

export function useCheckoutSubtotals() {
  const teamConfig = useTeamConfig();
  const isTotalAmountBreakdownEnabled = teamConfig?.purchaseSeatConfigs?.checkout?.totalAmountBreakdown?.isEnabled; // prettier-ignore
  const isCreditEnabled = Boolean(teamConfig?.availableCreditEnabled);
  const cartState = useAppStore(selectCartManager);
  const serviceFeeState = useAppStore(s => s?.data?.purchaseSeats?.serviceFee); // prettier-ignore
  const creditState = useAppStore((s) => s?.data?.purchaseSeats?.credit);
  const { nonMandatoryPerkTotalAmount: perkTotalPrice } = useBackendSubtotals();
  const { selectedPaymentPlan } = usePaymentPlans();

  // computed values
  const basePrice = (cartState?.basePrice || 0) - perkTotalPrice;
  const allInPrice = calcAllInPrice(cartState, serviceFeeState, selectedPaymentPlan, teamConfig); // prettier-ignore
  const creditToBeApplied = calcCreditToBeApplied(creditState, teamConfig);
  const amountDue = calcAmountDue( serviceFeeState, creditState, teamConfig); // prettier-ignore
  const remainingCredit = creditState?.creditInLedger - creditState?.creditToUse; // prettier-ignore

  const processingFee = serviceFeeState?.orderServiceFee || 0;
  const cartOrderServiceFee = cartState?.orderServiceFee || 0;

  const totalAmountBreakdown = serviceFeeState?.totalAmountBreakdown;

  return {
    basePrice,
    allInPrice,
    perkTotalPrice,
    creditToBeApplied,
    amountDue,
    remainingCredit,
    processingFee,
    cartOrderServiceFee,
    isCreditEnabled,
    totalAmountBreakdown,
    isTotalAmountBreakdownEnabled,
  };
}
